import { isSupportedCountry, parsePhoneNumberWithError, type CountryCode } from 'libphonenumber-js/max';

// Keep in sync with pipecat-server/services/phone_normalize.py. The browser
// supplies a hint only; ambiguous national numbers need a country code.
const ZONES: Record<string, CountryCode> = {
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Asia/Dubai": "AE",
  "Asia/Singapore": "SG",
  "Asia/Karachi": "PK",
  "Asia/Dhaka": "BD",
  "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR",
  "Asia/Shanghai": "CN",
  "Asia/Hong_Kong": "HK",
  "Asia/Taipei": "TW",
  "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID",
  "Asia/Manila": "PH",
  "Asia/Riyadh": "SA",
  "Asia/Jerusalem": "IL",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Colombo": "LK",
  "Europe/London": "GB",
  "Europe/Berlin": "DE",
  "Europe/Paris": "FR",
  "Europe/Dublin": "IE",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Amsterdam": "NL",
  "Europe/Zurich": "CH",
  "Europe/Stockholm": "SE",
  "Europe/Warsaw": "PL",
  "Europe/Istanbul": "TR",
  "Europe/Lisbon": "PT",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Edmonton": "CA",
  "America/Halifax": "CA",
  "America/Mexico_City": "MX",
  "America/Sao_Paulo": "BR",
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Perth": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Adelaide": "AU",
  "Pacific/Auckland": "NZ",
  "Africa/Johannesburg": "ZA",
  "Africa/Lagos": "NG",
  "Africa/Cairo": "EG"
};

export function browserPhoneRegion(): CountryCode | undefined {
  let timezone = '';
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* Site fallback. */ }
  if (ZONES[timezone]) return ZONES[timezone];
  const locale = typeof navigator !== 'undefined' ? navigator.language : '';
  return locale.replace(/_/g, '-').split('-').slice(1).map(part => part.toUpperCase())
    .find(part => isSupportedCountry(part)) as CountryCode | undefined;
}

/** Normalize before the form gate can release; the API still validates it. */
export function normalizeIntroPhone(raw: string | undefined, siteRegion?: string, hintRegion = browserPhoneRegion()): string | null {
  if (typeof raw !== 'string' || raw.length > 40 || /\p{C}/u.test(raw)) return null;
  const value = raw.normalize('NFKC').trim().replace(/^00/, '+');
  if (!/^\+?[0-9 ().-]{6,}$/.test(value)) return null;
  const parse = (region?: CountryCode): string | null => {
    try {
      const phone = parsePhoneNumberWithError(value, { defaultCountry: region, extract: false });
      return phone.isValid() && !phone.ext ? phone.number : null;
    } catch { return null; }
  };
  if (value.startsWith('+')) return parse();
  const regions = [hintRegion, siteRegion].filter((region): region is CountryCode => !!region && isSupportedCountry(region));
  const numbers = new Set([...new Set(regions)].map(parse).filter(Boolean));
  return numbers.size === 1 ? [...numbers][0]! : null;
}
